import React, { useEffect, useState } from 'react';
import { UserPlus, X } from 'lucide-react';
import BaseSelect from '../../Base/BaseSelect';
import type {
  ManagementItem,
  SuperintendencyItem,
  AreaItem,
} from '../../../services/useAdicionalCatalogService';
import './ElAbraGroupModal.css';

interface ElAbraGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: {
    management: number;
    superintendency: number;
    area: number;
  }) => void;
  loading?: boolean;
  initialValues?: {
    management?: number;
    superintendency?: number;
    area?: number;
  } | null;
  managements: ManagementItem[];
  superintendencies: SuperintendencyItem[];
  areas: AreaItem[];
  title?: string;
  submitLabel?: string;
  onAddCatalog?: (kind: 'management' | 'superintendency' | 'area') => void;
  selectedCatalogItem?: {
    kind: 'management' | 'superintendency' | 'area';
    id: number;
  } | null;
}

const ElAbraGroupModal: React.FC<ElAbraGroupModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  loading = false,
  managements,
  superintendencies,
  areas,
  title = 'Nuevo grupo El Abra',
  submitLabel = 'Guardar grupo',
  initialValues = null,
  onAddCatalog,
  selectedCatalogItem = null,
}) => {
  const [managementId, setManagementId] = useState<number | ''>('');
  const [superintendencyId, setSuperintendencyId] = useState<number | ''>('');
  const [areaId, setAreaId] = useState<number | ''>('');

  const selectedArea = areas.find(a => a.id === areaId);

  useEffect(() => {
    if (isOpen) {
      setManagementId(initialValues?.management ?? '');
      setSuperintendencyId(initialValues?.superintendency ?? '');
      setAreaId(initialValues?.area ?? '');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, initialValues]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!selectedCatalogItem) return;

    if (selectedCatalogItem.kind === 'management') {
      setManagementId(selectedCatalogItem.id);
    } else if (selectedCatalogItem.kind === 'superintendency') {
      setSuperintendencyId(selectedCatalogItem.id);
    } else {
      setAreaId(selectedCatalogItem.id);
    }
  }, [selectedCatalogItem]);

  const canSubmit =
    managementId !== '' && superintendencyId !== '' && areaId !== '';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      management: managementId as number,
      superintendency: superintendencyId as number,
      area: areaId as number,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="el-abra-group-modal-overlay" onClick={onClose}>
      <div className="el-abra-group-modal-content" onClick={e => e.stopPropagation()}>
        <div className="el-abra-group-modal-header">
          <h3 className="el-abra-group-modal-title">{title}</h3>
          <button type="button" className="el-abra-group-modal-close" onClick={onClose} aria-label="Cerrar">
            <X size={22} />
          </button>
        </div>
        <p className="el-abra-group-modal-hint">
          Agrupa gerencia, superintendencia y área para reutilizarlos al completar datos adicionales del servicio.
        </p>
        <form onSubmit={handleSubmit} className="el-abra-group-modal-form">
          <div className="el-abra-group-modal-field">
            <div className="el-abra-group-modal-field-head">
              <label>Gerencia</label>
              {onAddCatalog && (
                <button
                  type="button"
                  className="el-abra-group-modal-add-catalog"
                  onClick={() => onAddCatalog('management')}
                  disabled={loading}
                  title="Crear una nueva gerencia"
                >
                  <UserPlus size={15} strokeWidth={2.25} aria-hidden />
                  Nuevo
                </button>
              )}
            </div>
            <BaseSelect
              label="Gerencia"
              hideLabel
              name="management"
              value={String(managementId)}
              onChange={e => setManagementId(e.target.value === '' ? '' : Number(e.target.value))}
              placeholder="Seleccionar"
              preserveCase
              searchable
              disabled={loading}
              options={managements.map(m => ({ value: m.id, label: m.name }))}
            />
          </div>
          <div className="el-abra-group-modal-field">
            <div className="el-abra-group-modal-field-head">
              <label>Superintendencia</label>
              {onAddCatalog && (
                <button
                  type="button"
                  className="el-abra-group-modal-add-catalog"
                  onClick={() => onAddCatalog('superintendency')}
                  disabled={loading}
                  title="Crear una nueva superintendencia"
                >
                  <UserPlus size={15} strokeWidth={2.25} aria-hidden />
                  Nuevo
                </button>
              )}
            </div>
            <BaseSelect
              label="Superintendencia"
              hideLabel
              name="superintendency"
              value={String(superintendencyId)}
              onChange={e => setSuperintendencyId(e.target.value === '' ? '' : Number(e.target.value))}
              placeholder="Seleccionar"
              preserveCase
              searchable
              disabled={loading}
              options={superintendencies.map(s => ({ value: s.id, label: s.name }))}
            />
          </div>
          <div className="el-abra-group-modal-field">
            <div className="el-abra-group-modal-field-head">
              <label>Área y responsable</label>
              {onAddCatalog && (
                <button
                  type="button"
                  className="el-abra-group-modal-add-catalog"
                  onClick={() => onAddCatalog('area')}
                  disabled={loading}
                  title="Crear una nueva área y su responsable"
                >
                  <UserPlus size={15} strokeWidth={2.25} aria-hidden />
                  Nuevo
                </button>
              )}
            </div>
            <BaseSelect
              label="Área"
              hideLabel
              name="area"
              value={String(areaId)}
              onChange={e => setAreaId(e.target.value === '' ? '' : Number(e.target.value))}
              placeholder="Seleccionar"
              preserveCase
              searchable
              disabled={loading}
              options={areas.map(a => ({
                value: a.id,
                label: a.area_responsible ? `${a.name} (${a.area_responsible})` : a.name,
              }))}
            />
          </div>
          <div className="el-abra-group-modal-responsible">
            <span>Responsable del área:</span> {selectedArea?.area_responsible || '—'}
          </div>
          <div className="el-abra-group-modal-actions">
            <button type="button" className="el-abra-group-modal-btn secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="el-abra-group-modal-btn primary" disabled={loading || !canSubmit}>
              {loading ? 'Guardando…' : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ElAbraGroupModal;
